import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Wrench, LogOut, Search, RefreshCw, Inbox } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { STATUSES, STATUS_COLORS } from "@/lib/data";

const FILTERS = ["all", ...STATUSES];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/bookings");
      setBookings(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => { setUser(r.data); load(); })
      .catch(() => navigate("/admin/login"));
  }, [navigate, load]);

  const counts = useMemo(() => {
    const c = { all: bookings.length };
    for (const s of STATUSES) c[s] = bookings.filter((b) => b.status === s).length;
    return c;
  }, [bookings]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return bookings.filter((b) => {
      if (filter !== "all" && b.status !== filter) return false;
      if (!q) return true;
      return [b.name, b.phone, b.email, b.appliance, b.brand, b.address].filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }, [bookings, filter, search]);

  const setStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      const { data } = await api.patch(`/bookings/${id}`, { status });
      setBookings((bs) => bs.map((b) => (b.id === id ? data : b)));
      toast.success(`Request marked ${status}.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUpdatingId(null);
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* cookie cleanup best-effort */ }
    localStorage.removeItem("thortek_token");
    navigate("/admin/login");
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0F1215] flex items-center justify-center" data-testid="admin-dashboard-loading">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#606D7A]">Verifying access…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1215] text-[#F4F2EE]" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-[#0F1215]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-brand-link">
            <span className="w-9 h-9 rounded-sm bg-[#E06A26] flex items-center justify-center">
              <Wrench className="w-4.5 h-4.5 text-[#0F1215]" strokeWidth={2.5} />
            </span>
            <span className="font-display font-extrabold tracking-[0.18em]">THOR<span className="text-[#E06A26]">TEK</span></span>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#606D7A] hidden sm:inline">/ Dispatch console</span>
          </Link>
          <div className="flex items-center gap-5">
            <span className="font-mono text-[11px] text-[#94A0AC] hidden md:inline" data-testid="admin-user-email">{user.email}</span>
            <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 text-sm text-[#94A0AC] hover:text-[#E06A26] transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-6 mb-8">
          <div className="flex flex-wrap gap-3" data-testid="admin-dashboard-filter-status">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`admin-filter-${f}`}
                onClick={() => setFilter(f)}
                className={`px-5 py-2.5 rounded-full font-mono text-[11px] uppercase tracking-[0.2em] border transition-all duration-300 ${
                  filter === f
                    ? "bg-[#E06A26] border-[#E06A26] text-[#0F1215] font-semibold"
                    : "border-white/10 text-[#94A0AC] hover:border-[#E06A26]/40"
                }`}
              >
                {f} · {counts[f] ?? 0}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="w-4 h-4 text-[#606D7A] absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="admin-dashboard-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, phone, appliance…"
                className="bg-[#1E242B] border border-white/10 rounded-full pl-11 pr-5 py-2.5 text-sm w-64 outline-none focus:border-[#E06A26]/60 transition-colors"
              />
            </div>
            <button data-testid="admin-refresh-btn" onClick={load} className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-[#94A0AC] hover:text-[#E06A26] hover:border-[#E06A26]/40 transition-colors" aria-label="Refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {loading ? (
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#606D7A] py-20 text-center">Loading requests…</p>
        ) : visible.length === 0 ? (
          <div className="py-24 text-center" data-testid="admin-dashboard-empty">
            <Inbox className="w-10 h-10 text-[#606D7A] mx-auto mb-4" strokeWidth={1.25} />
            <p className="text-[#94A0AC]">No requests match this view.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {visible.map((b, i) => (
              <motion.div
                key={b.id}
                data-testid={`admin-dashboard-request-row-${b.id}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.3) }}
                className="rounded-2xl border border-white/10 bg-[#171B20]/90 p-6 sm:p-7 hover:border-[#E06A26]/25 transition-colors duration-300"
              >
                <div className="flex flex-wrap items-start justify-between gap-5">
                  <div className="min-w-[220px]">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-display text-lg font-semibold">{b.name}</h3>
                      <span className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full border ${STATUS_COLORS[b.status] || ""}`} data-testid={`admin-row-status-${b.id}`}>
                        {b.status}
                      </span>
                    </div>
                    <p className="font-mono text-[11px] text-[#606D7A] mt-2">
                      #{b.id.slice(0, 8)} · {new Date(b.created_at).toLocaleString()}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-10 gap-y-3 text-sm flex-1 min-w-[280px]">
                    <div>
                      <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#606D7A] mb-1">Appliance</p>
                      <p className="text-[#F4F2EE]/90">{b.appliance}{b.brand ? ` · ${b.brand}` : ""}</p>
                    </div>
                    <div>
                      <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#606D7A] mb-1">Window</p>
                      <p className="text-[#F4F2EE]/90">{b.preferred_date} · {b.preferred_time}</p>
                    </div>
                    <div>
                      <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#606D7A] mb-1">Contact</p>
                      <p className="text-[#F4F2EE]/90">{b.phone}</p>
                      {b.email && <p className="text-[#94A0AC] text-xs mt-0.5 break-all">{b.email}</p>}
                    </div>
                    <div>
                      <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#606D7A] mb-1">Address</p>
                      <p className="text-[#F4F2EE]/90">{b.address || "—"}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    <select
                      data-testid={`admin-row-status-select-${b.id}`}
                      value={b.status}
                      disabled={updatingId === b.id}
                      onChange={(e) => setStatus(b.id, e.target.value)}
                      className="bg-[#0F1215] border border-white/10 rounded-full px-4 py-2.5 text-xs font-mono uppercase tracking-[0.15em] outline-none focus:border-[#E06A26]/60 transition-colors"
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {b.status === "pending" && (
                      <button
                        data-testid="admin-dashboard-confirm-action"
                        onClick={() => setStatus(b.id, "confirmed")}
                        className="bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-400 font-mono text-[11px] uppercase tracking-[0.15em] px-4 py-2.5 rounded-full transition-colors"
                      >
                        Confirm
                      </button>
                    )}
                  </div>
                </div>
                {b.notes && (
                  <p className="mt-5 pt-5 border-t border-white/5 text-sm text-[#94A0AC] leading-relaxed">
                    <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#606D7A] block mb-1.5">Customer notes</span>
                    {b.notes}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
